import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { LandingNav } from '../components/LandingNav'
import { LandingFooter } from '../components/LandingFooter'
import { SEOSteps } from '../components/seo/SEOSteps'
import { SEOFreeBanner } from '../components/seo/SEOFreeBanner'
import { SEOWaitlist } from '../components/seo/SEOWaitlist'
import { SEOFAQ } from '../components/seo/SEOFAQ'
import './SEOErinnerungen.css'

const faq = [
  { q: 'Welche WhatsApp-Chats kann ich exportieren?', a: 'Alle Einzel- und Gruppenchats auf iOS und Android. Einfach den Chat öffnen, auf Mehr (⋮) tippen und „Chat exportieren" wählen.' },
  { q: 'Sind meine Daten sicher?', a: 'Deine Dateien werden ausschließlich zur PDF-Erstellung verwendet und nach 30 Tagen automatisch gelöscht. Kein Account, keine Weitergabe.' },
  { q: 'Was ist im kostenlosen PDF enthalten?', a: 'Die ersten 50 Nachrichten mit Fotos, Emojis und Original-Layout — mit einem dezenten ChatArchiv-Wasserzeichen.' },
  { q: 'Wie viele Nachrichten kann das vollständige PDF enthalten?', a: 'Unbegrenzt — egal ob 100 oder 100.000 Nachrichten. Das Premium-PDF enthält alles.' },
  { q: 'Funktioniert das auch mit Gruppen-Chats?', a: 'Ja, Gruppen-Chats werden genauso unterstützt wie Einzel-Chats, inklusive aller Teilnehmer und Medien.' },
  { q: 'Kann ich Videos auch im PDF haben?', a: 'Ja — Videos erscheinen im Premium-PDF als Vorschaubild (Thumbnail), Fotos werden vollständig eingebettet.' },
  { q: 'Wie lange habe ich Zugriff auf mein PDF?', a: 'Nach dem Kauf erhältst du einen Download-Link, der 30 Tage gültig ist.' },
]

export function SEOErinnerungen() {
  const navigate = useNavigate()
  const goHome = () => navigate('/')

  return (
    <>
      <Helmet>
        <title>WhatsApp Chat als Erinnerung bewahren – ChatArchiv.de</title>
        <meta name="description" content="Wandle deinen WhatsApp-Chat in ein wunderschönes PDF um — mit Fotos, Emojis und Original-Layout. Für Erinnerungen, die für immer halten." />
      </Helmet>

      <LandingNav onStart={goHome} />

      {/* Hero */}
      <section className="seo-er-hero">
        <div className="seo-er-hero-inner">
          <div className="seo-eyebrow">Erinnerungen für immer bewahren</div>
          <h1 className="seo-er-h1">
            Manche Gespräche sind{' '}
            <span className="seo-accent">zu wertvoll zum Vergessen.</span>
          </h1>
          <p className="seo-er-sub">
            Verwandle deinen WhatsApp-Verlauf in ein wunderschönes PDF — mit Fotos, Emojis und
            dem Original-Layout. Sicher, privat und für die Ewigkeit.
          </p>
          <div className="seo-er-actions">
            <button className="seo-btn-primary" onClick={goHome}>✓ Kostenlos testen</button>
            <div className="seo-er-trust">
              <span>PDF kostenlos (mit Wasserzeichen)</span>
              <span className="seo-trust-sep">·</span>
              <span>Vollständig für einmalig €4,99</span>
              <span className="seo-trust-sep">·</span>
              <span>Kein Abo</span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="seo-er-stories">
        <div className="seo-er-stories-inner">
          <h2 className="seo-er-stories-title">Warum Menschen ihre Chats bewahren</h2>
          <div className="seo-er-story-grid">
            {[
              { quote: 'Meine Oma ist letztes Jahr gestorben. Ihre letzten Nachrichten sind jetzt für immer als PDF gespeichert — das bedeutet mir unendlich viel.', author: 'Maria, Wien' },
              { quote: 'Nach 8 Jahren Fernbeziehung haben wir unsere schönsten Nachrichten als gedrucktes Buch verschenkt. Unvergesslich.', author: 'Stefan & Jana, München' },
              { quote: 'Ich wollte unsere Familien-Gruppe vor dem Handywechsel sichern. Hat perfekt funktioniert, alle 3.200 Nachrichten.', author: 'Karin, Zürich' },
            ].map((s, i) => (
              <div key={i} className="seo-er-story-card">
                <p className="seo-er-story-quote">„{s.quote}"</p>
                <div className="seo-er-story-author">— {s.author}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SEOSteps onStart={goHome} />

      <section className="seo-er-cta-section">
        <div className="seo-er-cta-inner">
          <SEOFreeBanner onStart={goHome} />
          <div className="seo-er-divider">oder</div>
          <SEOWaitlist />
        </div>
      </section>

      <section className="seo-er-content">
        <div className="seo-er-content-inner">
          <h2>WhatsApp-Chats als Erinnerung bewahren</h2>
          <p>
            WhatsApp-Gespräche sind mehr als nur Textnachrichten — sie sind Erinnerungen, die
            zu wichtigen Momenten des Lebens gehören. Ob Gespräche mit einem verstorbenen
            Familienmitglied, einer alten Freundschaft oder einem besonderen Lebensabschnitt:
            diese Nachrichten verdienen es, dauerhaft erhalten zu bleiben.
          </p>
          <p>
            ChatArchiv.de wandelt deinen WhatsApp-Exportordner in ein professionell gestaltetes
            PDF um — mit Sprechblasen, Fotos, Emojis und dem gewohnten Layout. Das Ergebnis
            sieht aus wie dein Chat, nur schöner und dauerhafter.
          </p>
          <h2>So exportierst du deinen WhatsApp-Chat</h2>
          <p>
            WhatsApp bietet eine eingebaute Export-Funktion: Öffne den gewünschten Chat, tippe
            auf die drei Punkte oben rechts (⋮), wähle „Mehr" und dann „Chat exportieren". Mit
            „Mit Medien" werden auch Fotos und Videos mitgeliefert. WhatsApp erstellt eine
            ZIP-Datei, die du auf ChatArchiv.de hochladen kannst.
          </p>
          <h2>Was macht ein gutes Chat-PDF aus?</h2>
          <p>
            Ein gutes Erinnerungs-PDF zeigt Nachrichten im Original-Layout mit Sprechblasen,
            Farben und Emojis. Fotos sind eingebettet, Datum und Uhrzeit sind klar erkennbar.
            ChatArchiv.de bietet außerdem Anpassungsoptionen für Farben und Layout — so dass
            jedes PDF einzigartig wird.
          </p>
        </div>
      </section>

      <SEOFAQ items={faq} />
      <LandingFooter />
    </>
  )
}
